import { Link } from 'react-router';
import { motion } from 'motion/react';
import { t, duration, chips, dateTime, localeTag, money } from '../i18n/index.ts';
import { useHome, type HomeData } from '../App.tsx';
import { ErrorView, Loading, Money, Status, useNow } from '../components/ui.tsx';
import { useSession } from '../lib/session.tsx';
import { TurnNotifyControl } from '../components/TurnNotify.tsx';

function greeting(hour: number): string {
  if (hour >= 5 && hour < 10) return t('home.morning');
  if (hour >= 10 && hour < 17) return t('home.day');
  if (hour >= 17 && hour < 23) return t('home.evening');
  return t('home.night');
}

const place = (from: number | null, to: number | null) => (from === null ? '–' : from === to ? `${from}.` : `${from}.–${to}.`);

/** Countdown ring: how much of the decision time is left. */
function TimeRing({ fraction, children }: { fraction: number; children: React.ReactNode }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const f = Math.max(0, Math.min(1, fraction));
  return (
    <div className="time-ring">
      <svg viewBox="0 0 80 80" aria-hidden="true">
        <circle cx="40" cy="40" r={r} className="time-ring-track" />
        <motion.circle
          cx="40"
          cy="40"
          r={r}
          className={`time-ring-fill ${f < 0.15 ? 'low' : ''}`}
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c * (1 - f) }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        />
      </svg>
      <div className="time-ring-label">{children}</div>
    </div>
  );
}

function TurnCard({ turn, now, index }: { turn: HomeData['myTurns'][number]; now: number; index: number }) {
  const paused = turn.tournamentStatus === 'paused';
  const left = turn.deadlineAt ? turn.deadlineAt - now : (turn.pausedRemainingMs ?? 0);
  const fraction = left / (turn.turnSeconds * 1000);
  const hours = Math.floor(Math.max(0, left) / 3_600_000);
  const minutes = Math.floor((Math.max(0, left) % 3_600_000) / 60_000);
  return (
    <motion.article
      className="turn-card"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, type: 'spring', stiffness: 260, damping: 26 }}
    >
      <TimeRing fraction={fraction}>
        <strong className="num">{hours > 0 ? `${hours} h` : `${minutes} min`}</strong>
        {hours > 0 && <span className="num">{minutes} min</span>}
      </TimeRing>
      <div className="turn-card-body">
        <span className="turn-card-kicker">{paused ? t('home.paused') : t('home.yourTurn')}</span>
        <h3>{turn.tournamentName}</h3>
        <p>
          {t('home.hand', { n: turn.handNo })}
          {turn.deadlineAt && !paused ? `. ${t('home.deadlineShort', { time: dateTime(turn.deadlineAt) })}` : ''}
        </p>
      </div>
      <Link className="btn btn-brass turn-card-cta" to={`/turnaukset/${turn.tournamentId}/poyta`}>
        {t('home.act')}
      </Link>
    </motion.article>
  );
}

function ActiveCard({ a, now, index }: { a: HomeData['active'][number]; now: number; index: number }) {
  const out = a.myEntryStatus !== 'playing';
  const share = a.myStack && a.totalChips ? a.myStack / a.totalChips : 0;
  const leader = a.chipLeaderStack && a.totalChips ? a.chipLeaderStack / a.totalChips : 0;
  return (
    <motion.li initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + index * 0.04 }}>
      <Link to={`/turnaukset/${a.id}/poyta`} className={`tcard ${out ? 'out' : ''} ${a.turn?.isMe ? 'mine' : ''}`}>
        <div className="tcard-head">
          <h3>{a.name}</h3>
          {a.status === 'paused' ? (
            <Status kind="paused">{t('home.paused')}</Status>
          ) : a.turn?.isMe ? (
            <Status kind="turn">{t('home.yourTurn')}</Status>
          ) : a.away ? (
            <Status kind="paused">{t('table.away')}</Status>
          ) : a.drawing ? (
            <Status kind="registration">{t('home.drawing')}</Status>
          ) : null}
        </div>
        {out ? (
          <p className="tcard-out">{t('home.outPlace', { place: place(a.myPlaceFrom, a.myPlaceTo) })}</p>
        ) : (
          <>
            <div className="tcard-stack">
              <span className="tcard-stack-value num">{chips(a.myStack ?? 0)}</span>
              <span className="tcard-stack-label">{t('home.chips')}</span>
              {a.myRank && (
                <span className="tcard-rank num" title={t('home.rankHelp')}>
                  {t('home.rank', { rank: a.myRank, of: a.playersLeft })}
                </span>
              )}
            </div>
            <div className="share-bar" role="img" aria-label={t('home.shareLabel', { pct: Math.round(share * 100) })}>
              <motion.span className="share-fill" initial={{ width: 0 }} animate={{ width: `${share * 100}%` }} transition={{ duration: 0.7, ease: 'easeOut' }} />
              {leader > share && <span className="share-leader" style={{ left: `${leader * 100}%` }} title={t('home.leader')} />}
            </div>
          </>
        )}
        <dl className="tcard-meta">
          <div>
            <dt>{t('home.players')}</dt>
            <dd className="num">
              {a.playersLeft}/{a.entrants}
            </dd>
          </div>
          <div>
            <dt>{t('home.blinds')}</dt>
            <dd className="num">
              {chips(a.level.sb)}/{chips(a.level.bb)}
            </dd>
          </div>
          <div>
            <dt>{t('home.handsPlayed')}</dt>
            <dd className="num">{a.handsPlayed}</dd>
          </div>
        </dl>
        {((a.turn && !a.turn.isMe) || a.newHandsSinceVisit > 0) && (
        <div className="tcard-foot">
          {a.turn && !a.turn.isMe ? (
            <span>
              {t('home.waitingOn', { name: a.turn.name })}
              {a.turn.deadlineAt ? <span className="hint"> ({duration(a.turn.deadlineAt - now)})</span> : null}
            </span>
          ) : (
            <span />
          )}
          {a.newHandsSinceVisit > 0 && <span className="new-badge">{t('home.newHandsShort', { n: a.newHandsSinceVisit })}</span>}
        </div>
        )}
      </Link>
    </motion.li>
  );
}

export function Home() {
  const home = useHome();
  const { me } = useSession();
  const now = useNow(home.data ? home.data.serverTime - home.dataUpdatedAt : 0, 15_000);
  if (home.isPending) return <Loading />;
  if (home.isError) return <ErrorView error={home.error} onRetry={() => home.refetch()} />;
  const d = home.data;
  const running = d.myTurns.filter((m) => m.tournamentStatus === 'running');
  const debtNet = d.debts.owedToMeRemainingCents - d.debts.iOweRemainingCents;
  const waiting = d.active.filter((a) => a.turn && !a.turn.isMe && a.myEntryStatus === 'playing');
  const firstName = me?.displayName.split(' ')[0] ?? '';
  const today = new Intl.DateTimeFormat(localeTag, { weekday: 'long', day: 'numeric', month: 'long' }).format(now);

  return (
    <div className="page home">
      <header className="home-hero">
        <div className="home-hero-text">
          <p className="home-date">{today}</p>
          <h1>
            {greeting(new Date(now).getHours())}, {firstName}
          </h1>
          <p className="home-sub">
            {running.length > 0 ? t('home.turnsWaiting', { n: running.length }) : waiting.length > 0 ? t('home.allDone') : t('home.quiet')}
          </p>
        </div>
        <div className="home-kpis">
          <Link to="/tilastot" className="kpi">
            <span className="kpi-label">{t('home.allTimeNet')}</span>
            <span className="kpi-value">
              <Money cents={d.allTime.netCents} signed />
            </span>
            <span className="kpi-sub">{t('home.allTimeSub', { n: d.allTime.tournaments, wins: d.allTime.wins })}</span>
          </Link>
          <Link to="/velat" className="kpi">
            <span className="kpi-label">{t('home.debtBalance')}</span>
            <span className="kpi-value">
              <Money cents={debtNet} signed />
            </span>
            <span className="kpi-sub">
              {t('home.debtSub', { owe: money(d.debts.iOweRemainingCents), owed: money(d.debts.owedToMeRemainingCents) })}
            </span>
          </Link>
        </div>
      </header>

      <section className="section home-turns" aria-labelledby="turns-h">
        <h2 id="turns-h" className="visually-hidden">
          {t('home.yourTurns')}
        </h2>
        {d.myTurns.length > 0 ? (
          <div className="turn-cards">
            {d.myTurns.map((turn, i) => (
              <TurnCard key={turn.turnId} turn={turn} now={now} index={i} />
            ))}
          </div>
        ) : (
          <div className="calm-card">
            <span className="calm-mark" aria-hidden="true">
              ♠
            </span>
            <div>
              <strong>{t('home.noTurnsTitle')}</strong>
              <TurnNotifyControl />
            </div>
          </div>
        )}
      </section>

      <section className="section" aria-labelledby="active-h">
        <div className="section-head">
          <h2 id="active-h">{t('home.active')}</h2>
          <Link to="/turnaukset">{t('home.allTournaments')}</Link>
        </div>
        {d.active.length === 0 ? (
          <div className="rows">
            <p className="empty">{t('home.noActive')}</p>
          </div>
        ) : (
          <ul className="tcards">
            {d.active.map((a, i) => (
              <ActiveCard key={a.id} a={a} now={now} index={i} />
            ))}
          </ul>
        )}
      </section>

      <div className="home-columns">
        <section className="section" aria-labelledby="open-h">
          <h2 id="open-h">{t('home.open')}</h2>
          {d.open.length === 0 ? (
            <div className="rows">
              <p className="empty">{t('home.noOpen')}</p>
            </div>
          ) : (
            <ul className="open-list">
              {d.open.map((o) => (
                <li key={o.id}>
                  <Link className="open-card" to={`/turnaukset/${o.id}`}>
                    <div className="open-card-main">
                      <h3>{o.name}</h3>
                      <div className="seats-bar" role="img" aria-label={t('tournament.seats', { registered: o.registered, capacity: o.capacity })}>
                        {Array.from({ length: o.capacity }, (_, i) => (
                          <span key={i} className={i < o.registered ? 'taken' : undefined} />
                        ))}
                      </div>
                      <span className="hint">
                        {t('tournament.seats', { registered: o.registered, capacity: o.capacity })}
                        {o.myStatus === 'registered' ? `, ${o.myTermsCurrent ? t('home.youreIn') : t('tournament.termsPending')}` : ''}
                      </span>
                    </div>
                    <div className="open-card-money">
                      <span className="open-buyin num">{money(o.buyInCents)}</span>
                      <span className="hint">{t('home.pool', { amount: money(o.buyInCents * o.capacity) })}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="section" aria-labelledby="recent-h">
          <h2 id="recent-h">{t('home.recent')}</h2>
          {d.recentFinished.length === 0 ? (
            <div className="rows">
              <p className="empty">{t('home.noRecent')}</p>
            </div>
          ) : (
            <ul className="result-list">
              {d.recentFinished.map((r) => (
                <li key={r.id}>
                  <Link to={`/turnaukset/${r.id}/tulokset`} className="result-item">
                    <span className={`place-badge ${r.placeFrom === 1 ? 'gold' : r.prizeCents > 0 ? 'paid' : ''}`}>{place(r.placeFrom, r.placeTo)}</span>
                    <span className="result-name">
                      {r.name}
                      {r.finishedAt && <span className="hint">{new Intl.DateTimeFormat(localeTag, { day: 'numeric', month: 'numeric' }).format(r.finishedAt)}</span>}
                    </span>
                    <Money cents={r.netCents} signed />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
